// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Universe Discworld - Main Export
 * 
 * A flat world carried by four elephants on the back of Great A'Tuin.
 * Where magic works, narrative causality is real, and the Librarian goes "Ook!"
 * Based on Terry Pratchett's Discworld series and Science of Discworld.
 */

import { starSystems, spaceStations } from './StarSystemsConfig';
import { warpGates, warpGatePositions } from './WarpGatesConfig';
import { DiscworldAliensPlanets } from './AliensConfig';

// Star system positions on the 3D map
const starPositions = {
  // Central Disc - starting area (well-connected hub)
  500: { x: 50, y: 50, z: 0 },   // DISCWORLD (center)
  501: { x: 45, y: 42, z: 5 },   // ROUNDWORLD (close to center)
  
  // Western arc - evenly spaced
  505: { x: 35, y: 55, z: 8 },   // LLAMEDOS (northwest)
  522: { x: 32, y: 48, z: 4 },   // SKUND FOREST (west-central)
  521: { x: 28, y: 52, z: -2 },  // WYRMBERG (west)
  506: { x: 25, y: 45, z: -6 },  // ÜBERWALD (southwest)
  
  // Northern arc - mountain regions (closer together)
  510: { x: 42, y: 58, z: 10 },  // LANCRE
  511: { x: 38, y: 62, z: 12 },  // RAMTOPS
  512: { x: 44, y: 54, z: 8 },   // BAD ASS
  516: { x: 48, y: 65, z: 14 },  // TSORT (north)
  508: { x: 42, y: 72, z: 16 },  // KRULL (far north edge)
  
  // Southern arc - coastal regions
  520: { x: 52, y: 38, z: 2 },   // QUIRM (south-central)
  513: { x: 58, y: 32, z: -4 },  // GENUA (south)
  
  // Eastern arc - ancient civilizations (better spacing)
  503: { x: 62, y: 52, z: 6 },   // KLATCH (east)
  514: { x: 65, y: 58, z: 10 },  // EPHEBE (northeast)
  515: { x: 68, y: 54, z: 8 },   // OMNIA (east)
  517: { x: 70, y: 48, z: 4 },   // DJELIBEYBI (southeast)
  
  // Far southeast - XXXX region (closer to main disc)
  502: { x: 72, y: 38, z: -2 },  // XXXX
  504: { x: 75, y: 42, z: 0 },   // FOURECKS MOON (near XXXX)
  
  // Counterweight Continent - far east (better connected)
  507: { x: 78, y: 52, z: 8 },   // COUNTERWEIGHT CONTINENT
  518: { x: 82, y: 56, z: 10 },  // HUNGHUNG (northeast)
  519: { x: 85, y: 50, z: 6 },   // BES PELARGIC (far east)
  
  // Dangerous outer region (closer - 50% distance from Bes Pelargic)
  509: { x: 87, y: 54, z: 9 },   // DUNGEON DIMENSIONS (far corner)
};

// Space station positions on the 3D map
const stationPositions = {
  600: { x: 48, y: 52, z: 3 },   // UNSEEN UNIVERSITY OBSERVATORY
  601: { x: 52, y: 48, z: -3 },  // PATRICIAN'S ORBITAL PLATFORM
  602: { x: 28, y: 38, z: -5 },  // IGOR'S SPARE PARTS DEPOT
  603: { x: 86, y: 52, z: 7 },   // DEATH'S DOMAIN OUTPOST (closer - 50% from Bes Pelargic)
  604: { x: 68, y: 72, z: 18 },  // COHEN'S BARBARIAN OUTPOST
};

export const universeDiscworld = {
  id: 'discworld',
  name: 'Discworld',
  description: 'A flat world where magic works and narrative causality is real',
  starSystems,
  spaceStations,
  starPositions,
  stationPositions,
  warpGates,
  warpGatePositions,
  aliens: DiscworldAliensPlanets,
};

export default universeDiscworld;